<template>
  <div>
    <q-linear-progress
      v-if="loading"
      color="primary"
      indeterminate
    />
    <div
      v-if="persons.length"
      class="row q-col-gutter-md"
    >
      <div
        v-for="person in persons"
        :key="person.id"
        class="col-xs-6 col-sm-4 col-md-3 col-lg-2"
      >
        <TrashedPersonCard
          :person="person"
          @restored="onPersonRestored"
        />
      </div>
    </div>

    <div
      v-else-if="!loading"
      class="text-center text-grey-6 q-pa-xl"
    >
      <q-icon
        name="fa fa-trash"
        size="64px"
        class="q-mb-md"
      />
      <div>Trash is empty</div>
    </div>

    <infinite-scroll
      :active="hasMore"
      :loading="loadingMore"
      class="q-mt-md"
      @load-more="loadMore"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Person } from '@/analysis/types';
import InfiniteScroll from '@/components/InfiniteScroll.vue';
import TrashedPersonCard from './TrashedPersonCard.vue';
import personRepository from './person-repository';

interface Data {
  persons: Person[];
  loading: boolean;
  loadingMore: boolean;
  total: number;
  offset: number;
}

const PAGE_SIZE = 24;

export default defineComponent({
  emits: ['restored', 'total-changed'],
  components: {
    InfiniteScroll,
    TrashedPersonCard,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      persons: [],
      loading: false,
      loadingMore: false,
      total: 0,
      offset: 0,
    };
  },
  computed: {
    hasMore(): boolean {
      return this.persons.length < this.total;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const result = await personRepository.findAll(this.clientId, {
          onlyTrashed: true,
          limit: PAGE_SIZE,
          offset: 0,
        });
        this.persons = result.items;
        this.total = result.total;
        this.offset = PAGE_SIZE;
        this.$emit('total-changed', this.total);
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.loadingMore = true;
      try {
        const result = await personRepository.findAll(this.clientId, {
          onlyTrashed: true,
          limit: PAGE_SIZE,
          offset: this.offset,
        });
        this.persons.push(...result.items);
        this.offset += PAGE_SIZE;
      } finally {
        this.loadingMore = false;
      }
    },

    onPersonRestored(person: Person) {
      this.persons = this.persons.filter(p => p.id !== person.id);
      this.total -= 1;
      this.$emit('total-changed', this.total);
      this.$emit('restored', person);
    },

  },
});
</script>
